/**
 * `bindings.time` — the period a widget measures (CT-5641).
 *
 * A non-field binding, and the only one of the six this builder authors. It
 * names a DATE/TIMESTAMP **column of the source** (not a query field), the
 * range it covers, and whether a dashboard's own date filter overrides that
 * range. The sync turns it into the `DATE_FROM`/`DATE_TO` parameters and the
 * temporal predicate of the generated WIDGET query (guide 12 §6).
 *
 * Twenty-eight of the 29 types declare it; `PROFILE_CARD` is the exception,
 * because one record has no period. It is optional on all of them — except in
 * practice for the series families, whose `display.fill_gaps` default (`ZERO`)
 * needs a range to know which buckets are missing. `requiresTimeBinding` says
 * which case a type is in rather than leaving the author to find out at
 * publish.
 *
 * ## The range is a tagged union
 *
 * `$defs/date_range` is a `oneOf` of five branches, each with
 * `additionalProperties: false`, so a key the chosen mode does not declare is
 * a rejected payload rather than an ignored hint — a `grain` left over from
 * `LAST_N` after switching to `ALL_TIME` fails the whole save. `dateRange`
 * builds the branch and nothing else, which is what keeps the form's leftover
 * state out of the wire.
 */

import { WIDGET_DATE_RANGE_MODES, WIDGET_TIME_GRAINS } from '../constants/widgetContract.js';

/** Grains `PERIOD_TO_DATE` accepts — a day has no to-date. */
const PERIOD_TO_DATE_GRAINS = Object.freeze(['MONTH', 'QUARTER', 'YEAR']);

/** `LAST_N`'s bounds, from the schema. */
const MIN_LAST_N = 1;
const MAX_LAST_N = 366;

/** What a widget covers when the author says nothing. */
export const DEFAULT_DATE_RANGE = Object.freeze({ mode: 'ALL_TIME' });

/** The grains each mode offers. The editor renders these; the save checks them. */
export function grainsForDateRangeMode(mode) {
  if (mode === 'PERIOD_TO_DATE') return PERIOD_TO_DATE_GRAINS;
  if (mode === 'CURRENT_PERIOD' || mode === 'LAST_N') return WIDGET_TIME_GRAINS;
  return Object.freeze([]);
}

/**
 * One `date_range` branch, with the keys that branch declares and no others.
 *
 * @param {string} mode One of `WIDGET_DATE_RANGE_MODES`.
 * @param {{ grain?: string, n?: number, includeCurrent?: boolean,
 *   from?: string, to?: string }} [options] Whatever the form holds; the keys
 *   this mode has no use for are dropped rather than carried.
 * @returns {object|null} `null` for a mode the schema does not know, which is
 *   a caller bug and must not reach the wire as a half-built range.
 */
export function dateRange(mode, { grain, n, includeCurrent, from, to } = {}) {
  if (!WIDGET_DATE_RANGE_MODES.includes(mode)) return null;

  if (mode === 'ALL_TIME') return { mode };
  if (mode === 'FIXED') return from && to ? { mode, from, to } : null;

  const grains = grainsForDateRangeMode(mode);
  if (!grains.includes(grain)) return null;
  if (mode !== 'LAST_N') return { mode, grain };

  const count = Number(n);
  if (!Number.isInteger(count) || count < MIN_LAST_N || count > MAX_LAST_N) return null;
  return {
    mode,
    grain,
    n: count,
    // Only written when the author said otherwise: the schema's default is
    // `true`, and restating it would claim an intent nobody expressed.
    ...(includeCurrent === false ? { include_current: false } : {}),
  };
}

/** Is this stored range one the schema accepts? Used on the way back in. */
export const isDateRange = (range) =>
  Boolean(range) && dateRange(range.mode, {
    grain: range.grain,
    n: range.n,
    includeCurrent: range.include_current,
    from: range.from,
    to: range.to,
  }) !== null;

/**
 * The `time` binding, or `null` when there is no column to hang it on.
 *
 * @param {{ field?: string, range?: object, followDashboardDateFilter?: boolean }} params
 *   `field` is the **source column's** alias; `range` comes from `dateRange`.
 */
export function buildTimeBinding({ field, range, followDashboardDateFilter } = {}) {
  if (!field) return null;
  const resolved = range && isDateRange(range) ? range : DEFAULT_DATE_RANGE;
  return {
    field,
    range: resolved,
    // `true` is the schema's default; see `include_current` above.
    ...(followDashboardDateFilter === false ? { follow_dashboard_date_filter: false } : {}),
  };
}

/**
 * A stored `time` binding → the shape a form holds.
 *
 * @param {object|null} bindings A saved `visualization_schema.bindings`.
 */
export function parseTimeBinding(bindings) {
  const time = bindings?.time;
  if (!time?.field) return null;
  return {
    field: time.field,
    range: isDateRange(time.range) ? time.range : DEFAULT_DATE_RANGE,
    followDashboardDateFilter: time.follow_dashboard_date_filter !== false,
  };
}

/**
 * Does this type need a `time` binding to publish?
 *
 * Not a rule of the contract — the slot is optional on all 28 types that have
 * it — but of the engine. `display.fill_gaps` defaults to `ZERO` on every
 * series family (`NULL` on the heatmap), it acts only on a date axis
 * (*"solo eje de fecha (x.time_grain)"*), and filling the gaps of a period
 * means knowing where the period starts. Guide 12 §7 lists the `time` binding
 * among `LINE_CHART`'s minimum valid configuration for exactly that reason.
 *
 * So the answer is yes when all three hold, and the author hears it before
 * the save rather than after it.
 *
 * @param {object|null} configSchema The type's `configuration_schema`.
 * @param {{ display?: object, bindings?: object }} [authored] What the author
 *   has said so far: the display options and the bindings already built.
 */
export function requiresTimeBinding(configSchema, { display = {}, bindings = {} } = {}) {
  if (!configSchema?.properties?.display?.properties?.fill_gaps) return false;
  // Absent means the type's own default, and no type defaults to `NONE`, so
  // silence is "filling" rather than "not".
  if (display.fill_gaps === 'NONE') return false;

  return Object.values(bindings).some((value) => {
    const items = Array.isArray(value) ? value : [value];
    return items.some((item) => Boolean(item?.time_grain));
  });
}

export default buildTimeBinding;
